import React, { useEffect, useState } from 'react';
import { 
  ArrowLeft, 
  Package, 
  User, 
  MapPin, 
  Calendar, 
  DollarSign, 
  Clock,
  Truck,
  CheckCircle,
  Phone,
  Mail,
  Copy
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getOrderById, updateOrderStatus } from '../../services/orderService';
import { Order } from '../../types';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

const OrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { user } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const data = await getOrderById(orderId);
      setOrder(data);
    } catch (error) {
      console.error('Error loading order:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusInfo = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return {
          icon: Clock,
          color: 'from-yellow-500 to-orange-500',
          bgColor: 'bg-yellow-50',
          textColor: 'text-yellow-800',
          label: 'Pending Approval'
        };
      case 'accepted':
        return {
          icon: CheckCircle,
          color: 'from-blue-500 to-blue-600',
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-800',
          label: 'Order Accepted'
        };
      case 'shipped':
        return {
          icon: Truck,
          color: 'from-purple-500 to-purple-600',
          bgColor: 'bg-purple-50',
          textColor: 'text-purple-800',
          label: 'Shipped'
        };
      case 'delivered':
        return {
          icon: CheckCircle,
          color: 'from-green-500 to-green-600',
          bgColor: 'bg-green-50',
          textColor: 'text-green-800',
          label: 'Delivered'
        };
      case 'rejected':
        return {
          icon: Clock,
          color: 'from-red-500 to-red-600',
          bgColor: 'bg-red-50',
          textColor: 'text-red-800',
          label: 'Rejected'
        };
    }
  };

 const handleStatusUpdate = async (newStatus: Order['status']) => {
  if (!order) return;
  
  const confirmMessage = 
    newStatus === 'shipped' ? 'Mark this order as shipped?' :
    newStatus === 'delivered' ? 'Mark this order as delivered?' :
    'Update order status?';
  
  if (!window.confirm(confirmMessage)) return;

  try {
    await updateOrderStatus(
      order.id, 
      newStatus, 
      order.expectedDeliveryDate, 
      order.buyerId  // Remove order.buyerName - only 4 params
    );
    await loadOrder();
  } catch (error) {
    console.error('Error updating status:', error);
    alert('Failed to update status');
  }
};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Package size={64} className="text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Order not found</p>
          <button
            onClick={() => navigate('/seller/orders')}
            className="mt-4 px-6 py-3 bg-primary text-white rounded-xl font-medium"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 pb-8">
      {/* Header */}
      <div className="bg-white shadow-md p-4 flex items-center gap-4 sticky top-0 z-10">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/seller/orders')}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft size={24} />
        </motion.button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-800">Order Details</h1>
          <p className="text-sm text-gray-500">#{order.id.slice(0, 12)}</p>
        </div>
      </div>

      <div className="p-4 max-w-4xl mx-auto">
        {/* Status Banner */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`${statusInfo.bgColor} rounded-2xl p-6 mb-6 shadow-lg`}
        >
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-16 h-16 bg-gradient-to-br ${statusInfo.color} rounded-2xl flex items-center justify-center shadow-xl`}>
              <StatusIcon className="text-white" size={32} />
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${statusInfo.textColor} opacity-80`}>Order Status</p>
              <h2 className={`text-2xl font-bold ${statusInfo.textColor}`}>{statusInfo.label}</h2>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className={`${statusInfo.bgColor} bg-opacity-50 rounded-xl p-3`}>
              <p className={`${statusInfo.textColor} opacity-70 mb-1`}>Order Date</p>
              <p className={`font-semibold ${statusInfo.textColor}`}>
                {format(order.createdAt, 'MMM dd, yyyy')}
              </p>
            </div>
            {order.expectedDeliveryDate && (
              <div className={`${statusInfo.bgColor} bg-opacity-50 rounded-xl p-3`}>
                <p className={`${statusInfo.textColor} opacity-70 mb-1`}>Expected Delivery</p>
                <p className={`font-semibold ${statusInfo.textColor}`}>
                  {format(order.expectedDeliveryDate, 'MMM dd, yyyy')}
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Artwork Details */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 mb-6 shadow-lg"
        >
          <div className="flex items-center gap-2 mb-4">
            <Package className="text-primary" size={24} />
            <h3 className="text-xl font-bold text-gray-800">Product Details</h3>
          </div>
          
          <div className="flex gap-4">
            <img
              src={order.artworkImage}
              alt={order.artworkTitle}
              className="w-32 h-32 object-cover rounded-xl shadow-md"
            />
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-gray-800 mb-2">{order.artworkTitle}</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Quantity:</span>
                  <span className="font-semibold text-gray-800">{order.quantity}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Price per unit:</span>
                  <span className="font-semibold text-gray-800">₹{order.price.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                  <DollarSign className="text-green-600" size={20} />
                  <span className="text-sm text-gray-600">Total Amount:</span>
                  <span className="text-2xl font-bold text-primary">₹{order.totalAmount.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Customer Information */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-6 mb-6 shadow-lg"
        >
          <div className="flex items-center gap-2 mb-4">
            <User className="text-primary" size={24} />
            <h3 className="text-xl font-bold text-gray-800">Customer Information</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {order.buyerName.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{order.buyerName}</p>
                  <p className="text-sm text-gray-600">Customer</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <Mail className="text-blue-600" size={20} />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">Email</p>
                  <p className="font-medium text-gray-800 text-sm">{order.buyerEmail}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(order.buyerEmail)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <Copy size={16} className="text-gray-600" />
                </button>
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <Phone className="text-green-600" size={20} />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">Phone</p>
                  <p className="font-medium text-gray-800 text-sm">{order.shippingAddress.phone}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(order.shippingAddress.phone)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <Copy size={16} className="text-gray-600" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Shipping Address */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-6 mb-6 shadow-lg"
        >
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="text-primary" size={24} />
            <h3 className="text-xl font-bold text-gray-800">Shipping Address</h3>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
            <div className="space-y-2">
              <p className="font-bold text-gray-800 text-lg">{order.shippingAddress.name}</p>
              <p className="text-gray-700">{order.shippingAddress.street}</p>
              <p className="text-gray-700">
                {order.shippingAddress.city}, {order.shippingAddress.state}
              </p>
              <p className="font-semibold text-gray-800">PIN: {order.shippingAddress.pincode}</p>
              {order.shippingAddress.landmark && (
                <p className="text-sm text-gray-600 mt-2">
                  <span className="font-medium">Landmark:</span> {order.shippingAddress.landmark}
                </p>
              )}
            </div>
            
            <button
              onClick={() => {
                const fullAddress = `${order.shippingAddress.name}\n${order.shippingAddress.street}\n${order.shippingAddress.city}, ${order.shippingAddress.state} - ${order.shippingAddress.pincode}`;
                copyToClipboard(fullAddress);
              }}
              className="mt-4 w-full py-3 bg-white text-primary rounded-xl font-medium hover:shadow-md transition-all flex items-center justify-center gap-2"
            >
              <Copy size={18} />
              {copied ? 'Copied!' : 'Copy Full Address'}
            </button>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="space-y-3"
        >
          {order.status === 'pending' && (
            <button
              onClick={() => navigate(`/seller/orders/${order.id}/accept`)}
              className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold text-lg hover:shadow-2xl transition-all flex items-center justify-center gap-2"
            >
              <CheckCircle size={24} />
              Accept Order & Set Delivery Date
            </button>
          )}

          {order.status === 'accepted' && (
            <button
              onClick={() => handleStatusUpdate('shipped')}
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl font-bold text-lg hover:shadow-2xl transition-all flex items-center justify-center gap-2"
            >
              <Truck size={24} />
              Mark as Shipped
            </button>
          )}

          {order.status === 'shipped' && (
            <button
              onClick={() => handleStatusUpdate('delivered')}
              className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold text-lg hover:shadow-2xl transition-all flex items-center justify-center gap-2"
            >
              <CheckCircle size={24} />
              Mark as Delivered
            </button>
          )}

          <button
            onClick={() => navigate('/seller/orders')}
            className="w-full py-4 border-2 border-gray-300 text-gray-700 rounded-2xl font-bold text-lg hover:bg-gray-50 transition-all"
          >
            Back to Orders
          </button>
        </motion.div>

        {/* Order Timeline */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl p-6 mt-6 shadow-lg"
        >
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="text-primary" size={24} />
            <h3 className="text-xl font-bold text-gray-800">Order Timeline</h3>
          </div>

          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="text-white" size={20} />
                </div>
                {order.status !== 'pending' && <div className="w-1 h-16 bg-green-500"></div>}
              </div>
              <div className="flex-1 pb-4">
                <p className="font-semibold text-gray-800">Order Placed</p>
                <p className="text-sm text-gray-600">{format(order.createdAt, 'MMM dd, yyyy HH:mm')}</p>
              </div>
            </div>

            {['accepted', 'shipped', 'delivered'].includes(order.status) && (
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="text-white" size={20} />
                  </div>
                  {['shipped', 'delivered'].includes(order.status) && <div className="w-1 h-16 bg-blue-500"></div>}
                </div>
                <div className="flex-1 pb-4">
                  <p className="font-semibold text-gray-800">Order Accepted</p>
                  <p className="text-sm text-gray-600">{format(order.updatedAt, 'MMM dd, yyyy HH:mm')}</p>
                </div>
              </div>
            )}

            {['shipped', 'delivered'].includes(order.status) && (
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                    <Truck className="text-white" size={20} />
                  </div>
                  {order.status === 'delivered' && <div className="w-1 h-16 bg-purple-500"></div>}
                </div>
                <div className="flex-1 pb-4">
                  <p className="font-semibold text-gray-800">Shipped</p>
                  <p className="text-sm text-gray-600">In transit</p>
                </div>
              </div>
            )}

            {order.status === 'delivered' && (
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="text-white" size={20} />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-800">Delivered</p>
                  <p className="text-sm text-gray-600">Order completed</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderDetail;